/**
 * @fileoverview Helper functions for admission experience UI model operations.
 */

import {history} from "../utils/history";
import {SlideBlock, UIState} from "../atoms/admission-explorer/model";

/**
 * Navigates to a specific page index in the admission experience.
 * Only works for non-admin routes.
 *
 * @param {number} [pageIndex=0] The page index to navigate to.
 * @param {boolean} [validate=false] Whether to enable validation mode.
 */
export const navigateToPageIndex = (pageIndex = 0, validate = false) => {
  if (!history.location.pathname.startsWith("/admin")) {
    const search = new URLSearchParams(history.location.search);

    search.set("page", `${pageIndex}`);

    if (validate) {
      search.set("validate", `${validate}`);
    }

    history.push({...history.location, search: search.toString()});
  }
};

/**
 * Gets the current page index from the URL search parameters.
 *
 * @returns {number} The current page index, or 0 if not found or invalid.
 */
export const getPageIndexFromUrl = () => {
  const search = new URLSearchParams(history.location.search);
  const pageIndex = parseInt(search.get("page"), 10);

  return isNaN(pageIndex) ? 0 : pageIndex;
};

/**
 * Finds the index of the next visible slide based on UI state.
 *
 * @param {UIState} uiState The current UI state.
 * @param {ReadonlyArray<SlideBlock>} slides Array of slide blocks.
 * @returns {number} The index of the next visible slide.
 */
export function findNextVisibleSlideIndex(
  uiState: UIState,
  slides: ReadonlyArray<SlideBlock>,
) {
  const index = slides.findIndex((slide, idx) => {
    if (idx <= uiState.slideIdx) {
      return false;
    }

    if (uiState.showIncompleteSlidesOnly && slide.state.visible) {
      return slide.state.complete === false || slide.state.flag !== null;
    }

    return slide.state.visible;
  });

  return Math.max(uiState.slideIdx, index);
}

/**
 * Finds the index of the previous visible slide based on UI state.
 *
 * @param {UIState} uiState The current UI state.
 * @param {ReadonlyArray<SlideBlock>} slides Array of slide blocks.
 * @returns {number} The index of the previous visible slide.
 */
export function findPreviousVisibleSlideIndex(
  uiState: UIState,
  slides: ReadonlyArray<SlideBlock>,
) {
  const currentIndex = uiState.slideIdx;
  const distanceToPreviousVisibleIndex = slides
    .filter((p, idx) => idx < currentIndex)
    .reverse()
    .findIndex(slide => {
      return uiState.showIncompleteSlidesOnly
        ? (slide.state.complete === false || slide.state.flag !== null) &&
            slide.state.visible
        : slide.state.visible;
    });
  const index = currentIndex - distanceToPreviousVisibleIndex - 1;

  return Math.max(0, index);
}
